import { palette } from '../../theme';
import { usePortalPalette } from '../../theme/usePortalPalette';
import {
  Badge,
  Box,
  CircularProgress,
  List,
  ListItemButton,
  Stack,
  Typography,
} from '../../primitives';
import * as Icons from '../../icons';
import { iconSize } from '@munifocus/tokens';

/** One row. Copy arrives already resolved to one language by the caller. */
export interface ConversationListItem {
  id: string;
  title: string;
  /** Newest message, truncated server-side. */
  preview: string | null;
  /** Pre-formatted by the caller — the app owns date formatting and locale. */
  timestamp: string | null;
  unreadCount: number;
  kind: 'direct' | 'group' | 'file' | 'support' | 'channel';
  muted?: boolean;
}

export interface ConversationListProps {
  items: ConversationListItem[];
  selectedId?: string | null;
  loading?: boolean;
  onSelect: (id: string) => void;
  labels: {
    empty: string;
    loading: string;
    /** Accessible suffix, e.g. "{{count}} unread" — the caller interpolates. */
    unread: (count: number) => string;
  };
}

/**
 * The list of threads, shared by the Officer and Admin portals.
 *
 * Presentational only, matching `NotificationBell`: rows and callbacks in,
 * every string supplied by the caller so each app localizes through its own i18n
 * context rather than this library growing one.
 *
 * Unread is carried by weight and a count, not colour alone — the same rule the
 * rest of the design system follows, and the reason a muted thread still shows
 * its number rather than being silently dimmed to nothing.
 */
export function ConversationList({
  items,
  selectedId,
  loading,
  onSelect,
  labels,
}: ConversationListProps) {
  const portal = usePortalPalette();

  if (loading) {
    return (
      <Stack sx={{ alignItems: 'center', py: 6 }} role="status" aria-label={labels.loading}>
        <CircularProgress size={22} />
      </Stack>
    );
  }

  if (items.length === 0) {
    return (
      <Stack sx={{ alignItems: 'center', gap: 1, py: 6, px: 3 }}>
        <Icons.MessageSquare size={iconSize.lg} color={palette.text.muted} aria-hidden />
        <Typography variant="body2" sx={{ color: palette.text.muted, textAlign: 'center' }}>
          {labels.empty}
        </Typography>
      </Stack>
    );
  }

  return (
    <List disablePadding>
      {items.map((item) => {
        const selected = item.id === selectedId;
        return (
          <ListItemButton
            key={item.id}
            selected={selected}
            onClick={() => onSelect(item.id)}
            sx={{
              alignItems: 'flex-start',
              gap: 1.5,
              px: 2,
              py: 1.5,
              borderBottom: `1px solid ${palette.border.line}`,
              '&.Mui-selected': { backgroundColor: palette.background.parch },
            }}
          >
            <Box sx={{ pt: 0.25, color: selected ? portal.brand.accent : palette.text.muted }}>
              <KindIcon kind={item.kind} />
            </Box>

            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Stack direction="row" sx={{ alignItems: 'baseline', gap: 1 }}>
                <Typography
                  variant="subtitle2"
                  sx={{
                    // Weight, not colour, carries unread — colour alone would not
                    // survive a greyscale print or a colour-vision difference.
                    fontWeight: item.unreadCount > 0 ? 700 : 500,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    flex: 1,
                  }}
                >
                  {item.title}
                </Typography>
                {item.timestamp && (
                  <Typography variant="caption" sx={{ color: palette.text.muted, flexShrink: 0 }}>
                    {item.timestamp}
                  </Typography>
                )}
              </Stack>

              <Stack direction="row" sx={{ alignItems: 'center', gap: 1, mt: 0.25 }}>
                <Typography
                  variant="body2"
                  sx={{
                    color: palette.text.muted,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    flex: 1,
                  }}
                >
                  {item.preview ?? ''}
                </Typography>

                {item.muted && (
                  <Icons.BellOff size={iconSize.sm} color={palette.text.muted} aria-hidden />
                )}

                {item.unreadCount > 0 && (
                  <Badge
                    badgeContent={item.unreadCount}
                    max={9}
                    aria-label={labels.unread(item.unreadCount)}
                    sx={{
                      flexShrink: 0,
                      mr: 1.5,
                      '& .MuiBadge-badge': {
                        position: 'static',
                        transform: 'none',
                        backgroundColor: portal.brand.accent,
                        color: palette.text.onDark,
                      },
                    }}
                  />
                )}
              </Stack>
            </Box>
          </ListItemButton>
        );
      })}
    </List>
  );
}

/**
 * A line-only glyph per thread kind — the quickest way to tell a file discussion
 * from a colleague, which matters most in the list where the title alone is
 * ambiguous ("14 Rideau Street" could be either).
 */
function KindIcon({ kind }: { kind: ConversationListItem['kind'] }) {
  const size = 16;
  if (kind === 'channel') return <Icons.Globe size={size} aria-hidden />;
  if (kind === 'group') return <Icons.Users size={size} aria-hidden />;
  if (kind === 'file') return <Icons.FileText size={size} aria-hidden />;
  if (kind === 'support') return <Icons.ShieldCheck size={size} aria-hidden />;
  return <Icons.User size={size} aria-hidden />;
}
